import React, {
  useContext, useEffect, useState
} from 'react';
import { useRouter } from 'next/router';
import env from '@lib/envs/env';
import UserContext from '@context/User/user-context';
import AddToBoardModalContext from '@context/Modal/add-to-board-modal-context';
import ExploreProductModalContext from '@context/Modal/explore-product-modal-context';
import boardItemTypes from '@constants/boardItemTypes';
import DefaultTemplate from '@components/shared/templates/DefaultTemplate/DefaultTemplate';
import HideAt from '@components/shared/conditional/HideAt/HideAt';
import ShowAt from '@components/shared/conditional/ShowAt/ShowAt';
import ResultsResponsive, { simpleItemVisitor } from '@components/shared/ResultsResponsive/ResultsResponsive';
import MobileAppConversionBanner
  from '@components/shared/MobileAppConversionBanner/MobileAppConversionBanner';
import getBoardName from '@lib/board/getBoardName';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import ShareButton from '@components/shared/buttons/ShareButton/ShareButton';
import ExpandedSaveButton from '@components/shared/buttons/ExpandedSaveButton/ExpandedSaveButton';
import SaveButton from '@components/shared/buttons/SaveButton/SaveButton';
import assetTypes, { ROOM_PHOTOGRAPHY, THREE_D_RENDERING } from '@constants/assetTypes';
import { boardTypes } from '@constants/boardTypes';
import OverlayButton from '@components/shared/buttons/OverlayButton/OverlayButton';
import Caret from '@components/shared/images/atoms/Caret/Caret';
import { UGLYNAMES } from '@constants/roomTypes';

import ShareModalContext, { SHARETYPE } from '@context/Modal/share-modal-context';
import DiscoverBoardCard from '@components/shared/DiscoverCard/DiscoverBoardCard';
import { isSavedBoard } from '@lib/board/isSavedBoard';

import { BoardAsset } from '@components/pages/Room/Home/board/Board';
import classnames from 'classnames';
import { useTheme } from '@components/theme';
import { IVendorVariantInternal } from '@models/VendorVariant/i-vendor-variant';
import AddedToCartConfirmationModalContext from '@context/Modal/added-to-cart-confirmation-modal-context';
import dynamic from 'next/dynamic';
import Button from '@components/shared/buttons/Button/Button';
import AssetCarousel from './components/AssetCarousel/AssetCarousel';
import DesignerInfo from './components/DesignerInfo/DesignerInfo';
import BoardDescription from './components/BoardDescription/BoardDescription';
import BoardTags from './components/BoardTags/BoardTags';
import BoardItemList from './components/BoardItemList/BoardItemList';
import styles from './DesignerBoard.module.scss';

const View360 = dynamic(
  () => import('@components/shared/View360/View360'),
  { ssr: false }
);
const { IOS_APP_URL_SCHEME_BASE } = env;

interface IDesignerBoardProps {
  designerBoard: any;
  boardRecommendations: any;
  user: any;
}

const DesignerBoard = ({
  designerBoard, boardRecommendations, user
}: IDesignerBoardProps) => {
  const { savedVendorVariantIds, savedAssetIds } = useContext(UserContext);
  const { open } = useContext(AddToBoardModalContext);
  const {
    open: productModalOpen,
    isClosed: isProductModalClosed,
    isDoneAddingToCart,
    onDoneAddingToCart,
  } = useContext(ExploreProductModalContext);
  const [triggeredProductModal, setTriggeredProductModal] = useState(false);
  const { open: openAddedToCartConfirmation } = useContext(AddedToCartConfirmationModalContext);
  const { open: openShareModal } = useContext(ShareModalContext);
  const [show360, setShow360] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const router = useRouter();
  const [unbounceBannerHeight, setUnbounceBannerHeight] = useState(0);

  const theme = useTheme();

  useEffect(() => {
    if (designerBoard && typeof window !== 'undefined') {
      const analyticsListId = Array.isArray(router.query.list)
        ? router.query.list[0]
        : router.query.list;
      if (analyticsListId) {
        // remove query param from url without reloading or messing up history stack
        router.replace(router.asPath.split('?')[0], undefined, { shallow: true });
      }
      handleTrackDesignerBoardViewed(designerBoard, analyticsListId);
    }
  }, [designerBoard, router]);

  const sphereImages = (designerBoard?.boardAssets || []).filter(
    ({ asset }:{asset:BoardAsset}) => Number(asset.assetTypeId) === assetTypes['Photo Sphere']
  ).map(({ asset }:{asset:BoardAsset}) => asset.url);

  const readOnly = user && (user.role === 'admin' || user.role === 'designer');

  const isFullBleed = (designerBoard?.boardAssets || []).some((ba: any) => ba.asset &&
    (ba.asset.assetTypeId === THREE_D_RENDERING || ba.asset.assetTypeId === ROOM_PHOTOGRAPHY));

  const metaData = getMetaTags(designerBoard);
  const boardName = getBoardName(
    designerBoard?.publicName,
    designerBoard?.subStyles,
    designerBoard?.roomType?.type,
  );
  const jsonSchema = buildJsonSchema(designerBoard, metaData);

  const handleShare = () => {
    openShareModal({
      modelId: designerBoard.id,
      shareType: SHARETYPE.DESIGNERBOARD,
      url: metaData.url,
    });
  };

  const saveSelectedAsset = () => {
    if (selectedAsset) {
      open({
        saveItemId: Number(selectedAsset.id),
        saveItemImageUrl: selectedAsset.url,
        saveItemType: boardItemTypes.ASSETS,
        sourceBoard: designerBoard,
        source: 'interior_design_ideas_board_details',
      });
    }
  };

  const goBack = () => {
    const prevPage = window.location.href;

    // eslint-disable-next-line
    router.back();
    setTimeout(() => {
      if (window.location.href === prevPage) {
        // eslint-disable-next-line
        router.push('/exp/interior-design-ideas');
      }
    }, 200);
  };

  const handleSaveBoard = (board: any) => () => {
    const {
      mainBoardAsset: {
        asset: {
          id: assetId,
          url: imageUrl,
        },
      },
    } = board;

    open({
      saveItemId: Number(assetId),
      saveItemImageUrl: imageUrl,
      saveItemType: boardItemTypes.ASSETS,
      source: 'interior_design_ideas_board_details',
      sourceBoard: board,
    });
  };

  const hasAssets = designerBoard.boardAssets && designerBoard.boardAssets.length > 0;

  const getCanonicalBoardUrl = () => `https://${env.DOMAIN_NAME}/interior-design-ideas/design-board/${designerBoard.canonicalBoardId}`;

  const squareTreatment = designerBoard.type === boardTypes.Idea;

  const navProps = {
    isFixed: true,
    isTransparent: true,
    isHiddenMobile: true,
  };

  const [selectedVendorVariant, setSelectedVendorVariant] =
    useState<IVendorVariantInternal | null>(null);

  const handleQuickViewModalContent = (product: any) => {
    setSelectedVendorVariant(product);
    setTriggeredProductModal(true);
    productModalOpen({
      readOnly,
      initialVendorVariantId: Number(product.id),
      source: 'interior_design_ideas_board_details',
      projectBoardCommission: designerBoard.type === boardTypes['Project Board'] ? {
        boardId: designerBoard.id,
        userId: designerBoard.userId,
      } : undefined,
    });
  };

  useEffect(() => {
    if (triggeredProductModal && isProductModalClosed && isDoneAddingToCart) {
      openAddedToCartConfirmation({ vendorVariant: selectedVendorVariant });
      onDoneAddingToCart(false);
      setTriggeredProductModal(false);
    }
  }, [isProductModalClosed, isDoneAddingToCart]);

  return (
    <DefaultTemplate
      canonicalLink={getCanonicalBoardUrl()}
      description={metaData.description}
      facebook={{
        site_name: 'Havenly',
        image: metaData.image,
        url: metaData.url,
        type: metaData.type,
        title: metaData.title,
        description: metaData.description,
        structured_image: metaData.structuredImage,
      }}
      initializeFacebook
      jsonSchema={jsonSchema}
      keywords={metaData.keywords}
      navProps={navProps}
      noIndex={!designerBoard.robotsVisible}
      title={metaData.title}
      checkUnbounceBannerHeight={
        (height) => setUnbounceBannerHeight(height)
      }
    >
      <div className={ styles.Root }>
        {
          !show360 && (
            <div className={ `${styles.BackButtonContainer} ${isFullBleed ? styles.isFullBleed : ''}` }>
              <OverlayButton
                className={ styles.BackButton }
                onClick={ goBack }
              >
                <i className={ styles.BackButtonCaretIcon } role="img" aria-label="Go Back">
                  <Caret />
                </i>
              </OverlayButton>
            </div>
          )
        }

        <div className={styles.ContainerCarousel}>
          {
            !show360 && hasAssets && (
              <AssetCarousel
                designerBoard={designerBoard}
                onSave={saveSelectedAsset}
                onSelected={(asset) => setSelectedAsset(asset)}
                readOnly={readOnly}
                saved={selectedAsset && savedAssetIds[selectedAsset.id]}
                aspectRatio={
                  squareTreatment
                    ? 1
                    : 10 / 16
                }
                wrapperStyles={
                  squareTreatment
                    ? { maxWidth: '600px', maxHeight: '600px', margin: '0 auto' }
                    : null
                }
              />
            )
          }

          {
            !show360 && !hasAssets && (
              <div className={styles.EmptyCarousel} />
            )
          }

          {
            (sphereImages.length > 0 && show360) && (
              <div className={styles.Container360}>
                <View360
                  images360={sphereImages}
                  onClose={() => {
                    setShow360(false);
                  }}
                />
              </div>
            )
          }

          {
            (sphereImages.length > 0 && !show360) && (
              <div className={styles.Show360}>
                <Button onClick={() => {
                  setShow360(true);
                }}
                >
                  Show 360
                </Button>
              </div>
            )
          }
        </div>
        <div className={ styles.Content }>

          <div className={ styles.BoardHeader }>
            <DesignerInfo
              className={ styles.DesignerInfo }
              designerBoard={ designerBoard }
            />
            <div className={ styles.ActionsWrapper }>
              {
                !readOnly && (
                  <>
                    <ShareButton
                      className={ styles.ShareButton }
                      onClick={ handleShare }
                    />
                    <HideAt breakpoint="desktop">
                      <SaveButton
                        className={ styles.SaveButton }
                        onClick={ saveSelectedAsset }
                        saved={ selectedAsset && savedAssetIds[selectedAsset?.id] }
                      />
                    </HideAt>
                    <ShowAt breakpoint="desktop">
                      <ExpandedSaveButton
                        className={ styles.ExpandedSaveButton }
                        onClick={ saveSelectedAsset }
                        saved={ selectedAsset && savedAssetIds[selectedAsset?.id] }
                      />
                    </ShowAt>
                  </>
                )
              }
            </div>
          </div>

          {
            (boardName && (
              <h1 className={ classnames(theme.styles.Title2, styles.BoardName) }>{ boardName }</h1>
            ))
            ||
            (<div className={ styles.BoardName}>&nbsp;</div>)
          }

          {
            !!designerBoard && (
              <>
                <BoardDescription description={ designerBoard.description } />

                <BoardTags
                  onTagClick={ onTagClick }
                  tags={ designerBoard.tags }
                />
              </>
            )
          }

          <BoardItemList
            boardId={ designerBoard.id }
            hasDescription={!!designerBoard.description}
            shoppingList={designerBoard.shoppingList}
            onSave={(product: any) => open({
              saveItemId: Number(product.id),
              saveItemImageUrl: product.image?.uri,
              saveItemType: boardItemTypes.VENDOR_VARIANTS,
              sourceBoard: designerBoard.id,
              source: 'interior_design_ideas_board_details',
            })}
            readOnly={ readOnly }
            savedVendorVariantIds={ savedVendorVariantIds }
            setQuickViewModalContent={ (product: any) => handleQuickViewModalContent(product) }
          />
        </div>

        {
          (boardRecommendations && boardRecommendations.length > 0) && (
            <div className={ styles.YouMayLoveBackgroundWrap }>
              <div className={ styles.YouMayLoveSection }>
                <h3 className={ classnames(theme.styles.Title1, styles.YouMayLoveTitle) }>
                  You may also like...
                </h3>

                <div className={ styles.Results }>
                  <ul className={ styles.ResultList }>
                    <ResultsResponsive
                      maxRows={1}
                      itemVisitorProvider={() => simpleItemVisitor(
                        boardRecommendations || [],
                        (item, size) => (
                          <DiscoverBoardCard
                            board={item}
                            saved={isSavedBoard(item, savedAssetIds)}
                            readOnly={readOnly}
                            size={size}
                            onSave={handleSaveBoard(item)}
                          />
                        ),
                      )}
                    />
                  </ul>
                </div>
              </div>
            </div>
          )
        }
      </div>
      <MobileAppConversionBanner
        iosDeeplinkPath={`${IOS_APP_URL_SCHEME_BASE}://havenly.com/explore/discover?designBoardId=${designerBoard.id}`}
        unbounceBannerHeight={unbounceBannerHeight}
      />
    </DefaultTemplate>
  );
};

function onTagClick(tag:any, index:number) {
  analyticsTrack(
    'tag_clicked',
    {
      tagId: tag.id,
      tagName: tag.title,
      tagOrder: index,
    },
  );
}

function handleTrackDesignerBoardViewed(designerBoard:any, analyticsListId: string | undefined) {
  analyticsTrack(
    'explore_designer_board_viewed',
    {
      designerId: designerBoard.designer?.id || 0,
      boardId: designerBoard.id,
      listId: analyticsListId || '',
      styleArray: designerBoard.subStyles?.map(({ name }: any) => name).join(', ') || [],
    },
  );
}

function getMetaTags(designerBoard: any) {
  const designerName = designerBoard.designer ? designerBoard.designer.firstName : '';

  const styleList = getStyleList(designerBoard);
  const roomType = getRoomType(designerBoard);

  let imageUrl = '';
  let structuredImage = null;

  if (designerBoard.boardAssets && designerBoard.boardAssets.length) {
    const primaryAsset = designerBoard.boardAssets[0];
    imageUrl = primaryAsset.asset.url;
    if ([THREE_D_RENDERING, ROOM_PHOTOGRAPHY].includes(primaryAsset.asset.assetTypeId)) {
      structuredImage = {
        url: imageUrl,
        secure_url: imageUrl,
        type: primaryAsset.asset.mimeType,
        alt: `${styleList} ${roomType} Design by Havenly Interior Designer ${designerName}`.trim(),
      };
    }
  }

  const description = `View this ${styleList} ${roomType} design from Havenly interior designer ${designerName}. Shop products and get inspiration to decorate your own space.`;

  const url = designerBoard.canonicalUrl ?
    `https://${env.DOMAIN_NAME}${designerBoard.canonicalUrl}` :
    `https://${env.DOMAIN_NAME}/interior-design-ideas/design-board/${designerBoard.id}`;

  const pageTitleBuilder = () => {
    // In case we don't have a board name or a designer, let's default to these values
    const boardName = getBoardName(
      designerBoard?.publicName,
      designerBoard?.subStyles,
      designerBoard?.roomType?.type,
    ) || 'Design';
    const designer = designerName || 'Lisa';

    return `${boardName} by ${designer} | Havenly`;
  };

  return {
    description: designerBoard.description || description,
    image: imageUrl,
    title: pageTitleBuilder(),
    type: 'website',
    keywords: ['interior design inspiration', 'interior designer', 'home decor'],
    url,
    structuredImage,
  };
}

function buildJsonSchema(
  designerBoard: any,
  metaData: any,
) {
  const tags = designerBoard.tags
    .map((tag: any) => tag.title)
    .join(', ');

  return {
    '@type': 'CreativeWork',
    creator: designerBoard.designer?.displayName || 'Havenly',
    url: metaData.url,
    about: metaData.description,
    headline: designerBoard.publicName || metaData.title,
    keywords: tags,
    thumbnailUrl: metaData.image,
    sourceOrganization: 'Havenly',
  };
}

function getStyleList(designerBoard: any) {
  return designerBoard.subStyles
    .map((substyle: any) => substyle.name)
    .join(', ');
}

function getRoomType(designerBoard: any) {
  let roomType = 'room';

  if (designerBoard.roomType &&
    !UGLYNAMES.includes(Number(designerBoard.roomType.id))) {
    roomType = designerBoard.roomType.type;
  }

  return roomType;
}

export default DesignerBoard;
